import {some} from '@danwang/option';
import {
  DepositoryType,
  useAccountQuery,
  useBalanceAnchorQuery,
  useBalanceAnchorsQuery,
  useSetAccountClosingAnchorMutation,
  useSetAccountOpeningAnchorMutation,
  useUpdateBalanceAnchorMutation,
} from '@transactions/graphql';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import {AlertTriangle, Flag, FlagOff, Trash2, Undo2} from 'lucide-react';
import {DateTime} from 'luxon';
import * as React from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {toDisplayBalance, toStoredBalance} from 'src/account/balanceDisplay';
import {AmountDisplay} from 'src/AmountDisplay';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import {Container, PageTitle} from 'src/DetailComponents';
import Section from 'src/DetailComponents/Section';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import InputGroup from 'src/InputGroup';
import AmountInput from 'src/inputs/AmountInput';
import Empty from 'src/ListView/Empty';
import Loading from 'src/Loading';
import {useModals} from 'src/Modals';
import RawTransactionList from 'src/RawTransactionList';
import type {Option} from '@danwang/option';

/**
 * Detail page for a single balance anchor (a user-recorded balance). The
 * anchor's reconciliation — a first-class record ending at this anchor — is
 * rendered alongside: the span (baseline, net change) and the transactions
 * it claims.
 *
 * Editing mirrors the server invariants: the note is always editable (it
 * never enters the reconciliation equation), while the date and balance are
 * editable only while no reconciliation references this anchor (as either
 * endpoint).
 * Synced Plaid readings have no detail page — they are write-once telemetry
 * managed from the account's Overview tab.
 */
export const BalanceAnchorDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const anchorId = params.id || '';
  const modals = useModals();
  const formatMoney = useMoneyFormatter();

  const {data, loading, refetch} = useBalanceAnchorQuery({
    variables: {anchorId},
    fetchPolicy: 'cache-and-network',
  });
  const anchor = data?.balanceAnchor;

  // The account supplies the currency and the credit-card sign convention. It
  // can't load until we know the anchor's account, hence skip.
  const {
    data: accountData,
    loading: accountLoading,
    refetch: refetchAccount,
  } = useAccountQuery({
    variables: {id: anchor?.accountId ?? ''},
    skip: !anchor,
  });
  const account = accountData?.account;
  const currency = account?.currency ?? 'USD';
  const isDepository = account?.__typename === 'DepositoryAccount';
  const isCreditCard =
    isDepository && account.depositoryType === DepositoryType.CreditCard;

  // The account's anchors, only to decide whether this one can be the opening
  // balance: the opener must be the account's earliest anchor. The server is
  // the source of truth (it also rejects earlier transactions/readings); this
  // just avoids offering an action that would always fail.
  const {data: anchorsData} = useBalanceAnchorsQuery({
    variables: {accountId: anchor?.accountId ?? ''},
    skip: !anchor,
  });
  // Require the anchor list to be loaded before judging "earliest"/"latest":
  // an empty fallback while it loads would call every anchor both, and offer
  // designations the server would reject.
  const loadedAnchors = anchorsData?.balanceAnchors;
  const isEarliestAnchor =
    loadedAnchors != null &&
    anchor != null &&
    !loadedAnchors.some((a) => a.date < anchor.date);
  const isLatestAnchor =
    loadedAnchors != null &&
    anchor != null &&
    !loadedAnchors.some((a) => a.date > anchor.date);
  const isOpeningAnchor =
    isDepository && anchor != null && account.openingAnchorId === anchor._id;
  const isClosingAnchor =
    anchor != null && account?.closingAnchorId === anchor._id;
  const isDesignated = isOpeningAnchor || isClosingAnchor;

  const isReconciled = anchor?.reconciliation != null;
  // Date and balance are locked while referenced by a reconciliation (both
  // endpoints) OR while this anchor is the account's designated opening or
  // closing balance — a designated anchor marks a boundary date and is
  // required to stay zero, so both fields are pinned until the designation is
  // cleared.
  const dateLocked = isReconciled || isDesignated;
  const balanceLocked = isReconciled || isDesignated;

  const [note, setNote] = React.useState('');
  const [date, setDate] = React.useState<DateTime | null>(null);
  const [balance, setBalance] = React.useState<Option<Money>>(
    some(Money.zero('USD')),
  );
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  // Seed the edit form once the anchor (and its account's currency) is
  // known, and re-seed after a save refetch.
  React.useEffect(() => {
    if (!anchor) return;
    setNote(anchor.note ?? '');
    setDate(parseDate(anchor.date));
    setBalance(
      some(
        Money.fromServer({
          amount: toDisplayBalance(anchor.balance, isCreditCard),
          currency,
        }),
      ),
    );
  }, [
    anchor?._id,
    anchor?.note,
    anchor?.date,
    anchor?.balance,
    isCreditCard,
    currency,
  ]);

  const [updateAnchor, {loading: saving}] = useUpdateBalanceAnchorMutation();

  const [setOpeningAnchor, {loading: settingOpening}] =
    useSetAccountOpeningAnchorMutation();
  const [setClosingAnchor, {loading: settingClosing}] =
    useSetAccountClosingAnchorMutation();
  const [designationError, setDesignationError] = React.useState<
    string | null
  >(null);

  // Designate this anchor as the account's opening balance (its inception
  // floor), or clear the designation. Refresh both the anchor and the account
  // so the badge and the account header stay consistent.
  const handleSetOpening = async (makeOpening: boolean) => {
    if (!anchor) return;
    setDesignationError(null);
    try {
      await setOpeningAnchor({
        variables: {
          accountId: anchor.accountId,
          anchorId: makeOpening ? anchor._id : null,
        },
      });
      await Promise.all([refetch(), refetchAccount()]);
    } catch (err) {
      setDesignationError(
        err instanceof Error ? err.message : 'Failed to update opening balance',
      );
    }
  };

  // The mirror action: designate this anchor as the account's closing
  // balance (marking the account closed), or clear it (reopening).
  const handleSetClosing = async (makeClosing: boolean) => {
    if (!anchor) return;
    setDesignationError(null);
    try {
      await setClosingAnchor({
        variables: {
          accountId: anchor.accountId,
          anchorId: makeClosing ? anchor._id : null,
        },
      });
      await Promise.all([refetch(), refetchAccount()]);
    } catch (err) {
      setDesignationError(
        err instanceof Error ? err.message : 'Failed to update closing balance',
      );
    }
  };

  useBreadcrumb({
    label: anchor ? `Balance on ${anchor.date}` : anchorId,
    fallback: anchor
      ? [
          {label: 'Accounts', href: '/accounts'},
          {label: 'Balances', href: `/accounts/${anchor.accountId}`},
        ]
      : [{label: 'Accounts', href: '/accounts'}],
  });

  if (!anchorId) {
    return <Empty>No balance selected</Empty>;
  }

  if (loading && !data) {
    return <Loading size="medium" />;
  }

  if (!anchor) {
    return <Empty>Balance anchor not found</Empty>;
  }

  // Hold the page until the account resolves: it carries the currency and the
  // credit-card sign convention. Rendering the editor with the fallbacks
  // (USD, isCreditCard=false) would seed and save a credit-card balance with
  // the wrong sign, persisting an amount owed as a positive asset.
  if (accountLoading && !accountData) {
    return <Loading size="medium" />;
  }

  const displayBalance = Money.fromServer({
    amount: toDisplayBalance(anchor.balance, isCreditCard),
    currency,
  });

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    if (balance.isEmpty) {
      setError('Please enter a balance amount');
      return;
    }
    // A referenced or designated anchor's date and balance are locked
    // server-side; send their current values so the note-only edit is
    // accepted rather than rejected.
    const storedBalance = balanceLocked
      ? anchor.balance
      : toStoredBalance(balance.get().toInt().amount, isCreditCard);
    const isoDate = dateLocked
      ? anchor.date
      : (date?.toISODate() ?? anchor.date);
    try {
      await updateAnchor({
        variables: {
          anchorId: anchor._id,
          date: isoDate,
          balance: storedBalance,
          note: note.trim() === '' ? null : note.trim(),
        },
      });
      setSaved(true);
      // Editing the newest anchor's balance updates Account.balance
      // server-side (non-Plaid accounts), so refresh the shared account cache
      // too — otherwise the account header stays stale on the way back.
      await Promise.all([refetch(), refetchAccount()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    }
  };

  const handleReconcile = () => {
    modals.reconcileAnchorModal.openModal({
      anchorId: anchor._id,
      accountId: anchor.accountId,
      anchorDate: anchor.date,
      anchorBalance: anchor.balance,
      currency,
      isCreditCard,
      onSuccess: () => void refetch(),
    });
  };

  const handleUnreconcile = () => {
    if (!anchor.reconciliation) return;
    modals.deleteReconciliationModal.openModal({
      reconciliationId: anchor.reconciliation._id,
      anchorDate: anchor.date,
      transactionCount: anchor.reconciliation.rawTransactions?.length ?? 0,
      onDeleted: () => void refetch(),
    });
  };

  const handleDelete = () => {
    modals.deleteBalanceEntryModal.openModal({
      kind: 'anchor',
      entryId: anchor._id,
      entryDate: anchor.date,
      // Deleting the newest anchor rolls Account.balance back server-side.
      // Refresh the cached account before leaving so the destination's
      // cache-first header doesn't show the deleted balance, then navigate
      // back to the account's balances tab — this row is gone.
      onDeleted: () => {
        void refetchAccount().finally(() =>
          navigate(`/accounts/${anchor.accountId}`),
        );
      },
    });
  };

  const reconciliation = anchor.reconciliation;
  // The reconcile mutation returns a reconciliation without the
  // rawTransactions (and baselineAnchor) fields this page reads. Apollo can
  // briefly replace the richer detail-query object in the cache with that
  // partial one before refetch() lands, so treat those fields as possibly
  // absent rather than dereferencing them.
  const claimedTransactions = reconciliation?.rawTransactions ?? [];
  const baseline = reconciliation?.baselineAnchor ?? null;
  const changeCents =
    baseline != null
      ? toDisplayBalance(anchor.balance, isCreditCard) -
        toDisplayBalance(baseline.balance, isCreditCard)
      : null;

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-8">
      <Container>
        <div className="flex flex-col gap-3">
          <PageTitle>Balance on {anchor.date}</PageTitle>
          <div className="flex flex-wrap items-center gap-2">
            {isReconciled && (
              <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">
                Reconciled ({claimedTransactions.length} txns)
              </span>
            )}
            {anchor.needsReview && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">
                <AlertTriangle className="size-3" />
                Review needed
              </span>
            )}
            {isOpeningAnchor && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-2 py-0.5 text-xs font-semibold text-white">
                <Flag className="size-3" />
                Opening balance
              </span>
            )}
            {isClosingAnchor && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-600 px-2 py-0.5 text-xs font-semibold text-white">
                <FlagOff className="size-3" />
                Closing balance
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {isReconciled ? (
              <Button variant="outline" onClick={handleUnreconcile}>
                <Undo2 className="mr-1 size-4" />
                Unreconcile
              </Button>
            ) : (
              <Button variant="outline" onClick={handleReconcile}>
                Reconcile
              </Button>
            )}
            {isOpeningAnchor ? (
              <Button
                variant="outline"
                disabled={settingOpening}
                onClick={() => void handleSetOpening(false)}
              >
                <Flag className="mr-1 size-4" />
                Clear opening balance
              </Button>
            ) : (
              isDepository &&
              isEarliestAnchor &&
              Math.round(anchor.balance) === 0 && (
                <Button
                  variant="outline"
                  disabled={settingOpening}
                  onClick={() => void handleSetOpening(true)}
                >
                  <Flag className="mr-1 size-4" />
                  Set as opening balance
                </Button>
              )
            )}
            {isClosingAnchor ? (
              <Button
                variant="outline"
                disabled={settingClosing}
                onClick={() => void handleSetClosing(false)}
              >
                <FlagOff className="mr-1 size-4" />
                Reopen account
              </Button>
            ) : (
              isLatestAnchor &&
              Math.round(anchor.balance) === 0 && (
                <Button
                  variant="outline"
                  disabled={settingClosing}
                  onClick={() => void handleSetClosing(true)}
                >
                  <FlagOff className="mr-1 size-4" />
                  Set as closing balance
                </Button>
              )
            )}
            {!isReconciled && !isDesignated && (
              <Button variant="destructive" onClick={handleDelete}>
                <Trash2 className="mr-1 size-4" />
                Delete
              </Button>
            )}
          </div>
          {designationError && (
            <span className="text-destructive text-sm">{designationError}</span>
          )}
        </div>

        <Section header="Details">
          <div className="flex flex-col gap-3 p-4">
            <InputGroup label="Date">
              {dateLocked ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span>{anchor.date}</span>
                  <span className="text-muted-foreground text-xs">
                    {isReconciled
                      ? 'Locked — unreconcile to change the date.'
                      : isOpeningAnchor
                        ? 'Locked — clear the opening balance to change the date.'
                        : 'Locked — reopen the account to change the date.'}
                  </span>
                </div>
              ) : (
                <DatePicker
                  selectedDate={date ?? parseDate(anchor.date)}
                  onChange={setDate}
                />
              )}
            </InputGroup>
            <InputGroup label={isCreditCard ? 'Amount owed' : 'Balance'}>
              {balanceLocked ? (
                <div className="flex flex-wrap items-center gap-2">
                  <AmountDisplay amount={displayBalance} variant="plain" inline />
                  <span className="text-muted-foreground text-xs">
                    {isReconciled
                      ? 'Locked — unreconcile to change the balance.'
                      : isOpeningAnchor
                        ? 'Locked — clear the opening balance to change it.'
                        : 'Locked — reopen the account to change it.'}
                  </span>
                </div>
              ) : (
                <AmountInput
                  value={balance}
                  onChange={setBalance}
                  currency={currency}
                />
              )}
            </InputGroup>
            <InputGroup label="Source">
              <span className="text-muted-foreground inline-block rounded-full border border-current px-2 py-0.5 text-xs">
                {anchor.source}
              </span>
            </InputGroup>
            <InputGroup label="Note">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Statement closing balance"
                className="border-border bg-background text-foreground focus:ring-ring w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:outline-none"
              />
            </InputGroup>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                disabled={saving}
                onClick={() => void handleSave()}
              >
                Save
              </Button>
              {saved && (
                <span className="text-muted-foreground text-sm">Saved.</span>
              )}
              {error && (
                <span className="text-destructive text-sm">{error}</span>
              )}
            </div>
          </div>
        </Section>

        {reconciliation ? (
          <>
            <Section header="Reconciliation">
              <div className="flex flex-col gap-3 p-4">
                <InputGroup label="Baseline">
                  {baseline ? (
                    <Link
                      to={`/balance-anchors/${baseline._id}`}
                      className="underline"
                    >
                      {baseline.date} ·{' '}
                      {formatMoney(
                        Money.fromServer({
                          amount: toDisplayBalance(
                            baseline.balance,
                            isCreditCard,
                          ),
                          currency,
                        }),
                      )}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground italic">
                      (baseline removed)
                    </span>
                  )}
                </InputGroup>
                {changeCents != null && (
                  <InputGroup label="Change">
                    <AmountDisplay
                      amount={Money.fromServer({amount: changeCents, currency})}
                      variant="delta"
                      inline
                    />
                  </InputGroup>
                )}
                <InputGroup label="Ending">
                  <AmountDisplay amount={displayBalance} variant="plain" inline />
                </InputGroup>
                <InputGroup label="Reconciled">
                  {DateTime.fromMillis(reconciliation.reconciledAt).toLocaleString(
                    DateTime.DATETIME_MED,
                  )}
                </InputGroup>
              </div>
            </Section>

            <Section header={`Transactions (${claimedTransactions.length})`}>
              {claimedTransactions.length > 0 ? (
                <RawTransactionList transactions={claimedTransactions} />
              ) : (
                <div className="text-muted-foreground p-4 text-sm">
                  No transactions — the balance was unchanged since the baseline.
                </div>
              )}
            </Section>
          </>
        ) : (
          <Section header="Reconciliation">
            <div className="text-muted-foreground p-4 text-sm">
              Not reconciled. Reconcile it against the account transactions to
              lock in the balance change since the last anchor.
            </div>
          </Section>
        )}
      </Container>
    </div>
  );
};
